<template>
    <div v-click-outside="handleClickOutside" class="relative max-sm:w-full">
        <SearchFilterButton
            :buttonText="btnLabel"
            :selectedCounter="true"
            :selected="selected"
            :disabled="disabled || isDisabled"
            @clicked="handleClick"
        />
        <transition
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <ul
                v-show="open"
                class="w-60 absolute left-0 bottom-50 overflow-auto rounded-md bg-white text-base shadow-sm border border-slate-200 sm:text-sm p-2 mt-2 z-10"
            >
                <li
                    class="text-gray-900 relative select-none py-2 pl-3 pr-9 hover:bg-gray-100 rounded-md cursor-pointer"
                    v-for="(option, index) in options"
                    :key="option.name"
                    :ref="(el) => (optionRefs[index] = el)"
                    tabindex="0"
                    @keydown.enter.prevent="
                        () => handleSelect(option[valueKey])
                    "
                    @keydown.up="focusPreviousOption(index)"
                    @keydown.down="focusNextOption(index)"
                >
                    <label class="flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            :checked="selected.includes(option[valueKey])"
                            class="form-checkbox"
                            :value="option[valueKey]"
                            tabindex="-1"
                            @change="() => handleSelect(option[valueKey])"
                        />
                        <img
                            class="h-auto w-5 ml-3"
                            :alt="option.slug"
                            :src="`/images/${option.image}`"
                            v-if="showOptionImages && option.image"
                        />
                        <span class="ml-3">
                            {{ option[labelKey] }}
                        </span>
                    </label>
                </li>
                <li
                    v-if="selected.length"
                    class="text-orange-600 relative cursor-pointer select-none pt-2 pl-3 pr-9"
                >
                    <button
                        role="button"
                        @click="handleResetAll"
                        @keydown.enter.prevent="handleResetAll"
                    >
                        Reset All
                    </button>
                </li>
            </ul>
        </transition>
    </div>
</template>
<script>
import { ref, computed, reactive } from 'vue';
import { useAlertsStore } from '@/store/alerts';
import SearchFilterButton from '@/components/hotels/search/search-filter-button/search-filter-button-2.vue';

export default {
    components: {
        SearchFilterButton,
    },
    props: {
        origin: {
            type: String,
            default: 'search',
        },
        options: {
            type: Array,
            default: () => [],
        },
        btnLabel: {
            type: String,
            default: 'Filter Group',
        },
        disableOnSelectProperty: {
            type: Boolean,
            default: false,
        },
        isDisabled: {
            type: Boolean,
            default: false,
        },
        labelKey: {
            type: String,
            default: 'label',
        },
        valueKey: {
            type: String,
            default: 'name',
        },
        selected: {
            type: Array,
            required: true,
        },
        showOptionImages: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['selectionMade', 'clearSelected'],
    setup(props, { emit }) {
        const alertsStore = useAlertsStore();
        const open = ref(false);
        const optionRefs = reactive({});

        const handleSelect = (val) => {
            emit('selectionMade', val);
        };

        const handleResetAll = () => {
            emit('clearSelected');
        };

        const handleClick = () => {
            open.value = !open.value;
        };

        const handleClickOutside = () => {
            open.value = false;
        };

        const focusPreviousOption = (index) => {
            if (index > 0) {
                optionRefs[index - 1].focus();
            }
        };

        const focusNextOption = (index) => {
            if (index < props.options.length - 1) {
                optionRefs[index + 1].focus();
            }
        };

        const disabled = computed(() => {
            if (!props.disableOnSelectProperty) {
                return false;
            } else {
                return alertsStore.selectedProperties.length > 0;
            }
        });

        return {
            disabled,
            focusNextOption,
            focusPreviousOption,
            handleClick,
            handleClickOutside,
            handleSelect,
            handleResetAll,
            open,
            optionRefs,
        };
    },
};
</script>
